import { memo, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import { useTheme } from '@mui/material/styles';
import type { MarkdownHeadingProps } from '../../types';

const VARIANTS = {
  1: 'h1',
  2: 'h2',
  3: 'h3',
  4: 'h4',
  5: 'h5',
  6: 'h6',
} as const;

/**
 * Heading with a hover anchor that deep-links to the section and updates the
 * address bar without a full navigation.
 */
export const MarkdownHeading = memo(function MarkdownHeading({
  level,
  id,
  children,
}: MarkdownHeadingProps) {
  const theme = useTheme();
  const tag = VARIANTS[level] ?? 'h2';

  const handleAnchorClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (!id) return;
      event.preventDefault();
      const target = document.getElementById(id);
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.pushState(null, '', `#${id}`);
    },
    [id],
  );

  return (
    <Typography
      component={tag}
      variant={tag}
      id={id}
      sx={{
        '&:hover .heading-anchor': { opacity: 1 },
        '& .heading-anchor:focus-visible': { opacity: 1 },
      }}
    >
      {children}
      {id && (
        <Tooltip title="Link to this section">
          <Box
            component="a"
            href={`#${id}`}
            onClick={handleAnchorClick}
            className="heading-anchor"
            aria-label={`Link to ${id.replace(/-/g, ' ')}`}
            sx={{
              opacity: 0,
              ml: 1,
              color: theme.palette.text.disabled,
              border: 0,
              display: 'inline-flex',
              verticalAlign: 'middle',
              transition: theme.transitions.create(['opacity', 'color'], { duration: 150 }),
              '&:hover': { color: theme.palette.primary.main },
            }}
          >
            <LinkRoundedIcon sx={{ fontSize: '0.7em' }} />
          </Box>
        </Tooltip>
      )}
    </Typography>
  );
});

export default MarkdownHeading;
